// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import type { StrongExtract } from '@evolvemep/api-ts';
import type { Badge } from '@mantine/core';

import { StatusBadge, type BadgeSize } from 'components/Mantine/StatusBadge';
import type { CustomStatusEntityName, StateNameFor, StatusIdFor } from 'components/Mantine/StatusEditor/types';
import { useCustomStatuses } from 'components/Mantine/StatusEditor/useCustomStatuses';
import { isNotNil, isNil } from 'helpers/isNotNil';

export type GetLabelAndColorArgs<T extends CustomStatusEntityName> = {
  customStatusEntityName: T;
  /** If `stateName` is `null`, will return `null` */
  stateName: StateNameFor<T> | null;
  statusId?: StatusIdFor<T> | null;
};

export type EntityStatusProps<T extends CustomStatusEntityName> = GetLabelAndColorArgs<T> & {
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

export type EntityStatusBadgeProps<T extends CustomStatusEntityName> = EntityStatusProps<T> & {
  size?: StrongExtract<BadgeSize, 'lg' | 'xl' | 'xxl'>;
  progress?: {
    value: number;
    outOf: number;
  };
} & Partial<Badge.Props>;

/**
 * Renders a read-only Status Badge for an Entity with custom statuses.
 * Editable statuses are handled by {@link EditableStatusBadge}.
 */
export const EntityStatusBadge = <T extends CustomStatusEntityName>({
  customStatusEntityName,
  stateName,
  statusId,
  ...props
}: EntityStatusBadgeProps<T>) => {
  const { getLabelAndColor } = useCustomStatuses(customStatusEntityName);
  if (isNil(stateName)) return null;
  return <StatusBadge autoContrast={isNotNil(statusId)} {...getLabelAndColor(stateName, statusId)} {...props} />;
};
